/*
 * Brand indirection tokens.
 *
 * These are the only color slots a partner owns. Quartz owns everything else,
 * so a partner value always wins over the Quartz token for these slots.
 * The Quartz variant is selected by the `ENABLE_QUARTZ_COLORS` flag; the legacy
 * variant resolves to the same values the platform shipped before Quartz.
 */

import { COLOR_TOKENS } from 'constants/quartz/color_tokens';
import { PRIMITIVE_COLORS } from 'constants/quartz/primitive_colors';
import { ENABLE_QUARTZ_COLORS } from 'constants/api';
import { getMigratedColors, isQuartzColorsEnabled } from 'constants/quartz/migrated_colors';

/**
 * The partner-owned color slots, keyed by branding config attribute.
 *
 * @type {string[]}
 */
export const BRAND_SLOTS = [
    'avatarBackgroundColor',
    'linkTextColor',
    'navigationHighlightColor',
    'primaryColor',
    'primaryTextColor',
    'secondaryColor',
    'secondaryTextColor',
    'topNavAccentColor',
    'topNavColor',
    'topNavTextColor',
];

const LEGACY_COLORS = getMigratedColors(false);

export const LEGACY_BRAND_TOKENS = {
    avatarBackgroundColor: LEGACY_COLORS.tan,
    linkTextColor: LEGACY_COLORS.link,
    navigationHighlightColor: LEGACY_COLORS.tan,
    primaryColor: LEGACY_COLORS.blue,
    primaryTextColor: LEGACY_COLORS.black,
    secondaryColor: LEGACY_COLORS.lightblue,
    secondaryTextColor: LEGACY_COLORS.black,
    topNavAccentColor: LEGACY_COLORS.brandPeach2,
    topNavColor: LEGACY_COLORS.tan,
    topNavTextColor: LEGACY_COLORS.black,
};

export const QUARTZ_BRAND_TOKENS = {
    ...LEGACY_BRAND_TOKENS,
    avatarBackgroundColor: COLOR_TOKENS.background.tertiaryDefault,
    linkTextColor: COLOR_TOKENS.text.link,
    navigationHighlightColor: COLOR_TOKENS.background.tertiaryDefault,
    primaryColor: PRIMITIVE_COLORS.primary.aqua[500],
    primaryTextColor: COLOR_TOKENS.text.primary,
    secondaryColor: PRIMITIVE_COLORS.primary.aqua[200],
    secondaryTextColor: COLOR_TOKENS.text.primary,
    topNavAccentColor: PRIMITIVE_COLORS.brand.orange[100],
    topNavColor: COLOR_TOKENS.background.tertiaryDefault,
    topNavTextColor: COLOR_TOKENS.text.primary,
};

/**
 * Resolve the brand tokens for a given flag value.
 *
 * @method getBrandTokens
 * @param  {string|boolean} flagValue The `ENABLE_QUARTZ_COLORS` flag value
 * @return {Object}                   The brand tokens
 */
export const getBrandTokens = flagValue =>
    isQuartzColorsEnabled(flagValue) ? QUARTZ_BRAND_TOKENS : LEGACY_BRAND_TOKENS;

export const BRAND_TOKENS = getBrandTokens(ENABLE_QUARTZ_COLORS);
